import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
  useDeferredValue,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  useActivityActions,
  useActivityChatData,
} from "../contexts/ActivityContext";
import ChatListItem from "../components/ChatListItem";
import {
  SearchIcon,
  UsersIcon,
  MessageCircleIcon,
} from "../components/icons/FeatherIcons";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { type User, type Chat, type ActivityRequest } from "../types";
import { useToast } from "../contexts/ToastContext";
import { usePageTracking } from "../hooks/usePageTracking";
import { fallbackImage } from "../utils/getCachedImage";
import { useCachedImage } from "../hooks/useCachedImage";
import { useChatParticipants } from "../hooks/useChatParticipants";
import {
  readChatDrafts,
  type ChatDraftRecord,
  type ChatDraftsByChatId,
} from "../utils/chatDrafts";

import { crashlytics } from "../services/firebase";

const CHAT_ACTIVITY_CACHE_TTL_MS = 5 * 60 * 1000;
const CHAT_PAGE_ACTIVE_TAB_STORAGE_KEY = "chat-page-active-tab";

const getStoredChatPageTab = (): "groups" | "chats" | null => {
  try {
    const stored = window.sessionStorage.getItem(CHAT_PAGE_ACTIVE_TAB_STORAGE_KEY);
    if (stored === "groups" || stored === "chats") return stored;
    return null;
  } catch {
    return null;
  }
};

const resolveInitialChatPageTab = (
  locationState: unknown,
): "groups" | "chats" => {
  const state = locationState as { initialTab?: "groups" | "chats" } | null;
  if (state?.initialTab === "groups" || state?.initialTab === "chats") {
    return state.initialTab;
  }

  return getStoredChatPageTab() || "groups";
};

type ChatActivityCacheEntry = {
  value: ActivityRequest | null;
  fetchedAt: number;
};

const chatPageActivityCache = new Map<string, ChatActivityCacheEntry>();
const chatPageActivityInFlight = new Map<string, Promise<ActivityRequest | null>>();

const getCachedChatActivity = (activityId: string): ActivityRequest | null | undefined => {
  const cached = chatPageActivityCache.get(activityId);
  if (!cached) return undefined;

  const isExpired = Date.now() - cached.fetchedAt > CHAT_ACTIVITY_CACHE_TTL_MS;
  if (isExpired) {
    chatPageActivityCache.delete(activityId);
    return undefined;
  }

  return cached.value;
};

const setCachedChatActivity = (
  activityId: string,
  value: ActivityRequest | null,
) => {
  chatPageActivityCache.set(activityId, {
    value,
    fetchedAt: Date.now(),
  });
};

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error);
};

const ActiveNowCircle: React.FC<{ user: User }> = React.memo(({ user }) => {
  const navigate = useNavigate();
  const { createOrGetDirectChat } = useActivityActions();
  const { addToast } = useToast();
  const profileImage = useCachedImage(
    user.profileImageUrl,
    user.username,
    user.id,
  );

  const handleUserClick = async () => {
    try {
      const chatId = await createOrGetDirectChat(user.id);
      navigate(`/chat/${chatId}`, {
        state: {
          returnToPath: "/chat",
          returnToState: { initialTab: "chats" },
        },
      });
    } catch (error) {
      crashlytics.logError(
        `Error creating or getting chat with this friend: ${JSON.stringify(getErrorMessage(error))}`,
        "ChatPage.tsx",
        error,
      );
      addToast("Could not create a chat with this user.", "error");
    }
  };

  return (
    <button
      onClick={handleUserClick}
      className="flex-shrink-0 flex flex-col items-center space-y-0.5 focus:outline-none group w-12"
    >
      <div className="relative w-10 h-10">
        <img
          loading="lazy"
          onError={handleImageError}
          className="w-full h-full object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
          src={profileImage}
          alt={user.username}
        />
        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border border-white shadow-sm"></div>
      </div>
      <p className="text-xs text-gray-500 truncate w-full text-center font-normal">
        {user.username.split(" ")[0]}
      </p>
    </button>
  );
});

const EmptyState: React.FC<{ title: string; message: string }> = ({
  title,
  message,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="text-center text-gray-500 pt-16 px-4 flex flex-col items-center"
  >
    <UsersIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
    <h2 className="font-bold text-lg text-gray-800">{title}</h2>
    <p className="text-sm mt-1">{message}</p>
  </motion.div>
);

const ChatPage: React.FC = () => {
  usePageTracking("ChatList", "ChatPage");
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, getUsersByIds, getAllUsers } = useAuth();
  const {
    getChatsForUser,
    getUnreadMessageCount,
    getUnreadMessageCountForChat,
    getUnreadGroupChatCount,
    getUnreadDirectChatCount,
  } = useActivityChatData();
  const {
    createOrGetDirectChat,
    refreshSingleActivity,
  } = useActivityActions();

  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [activeTab, setActiveTab] = useState<"groups" | "chats">(() =>
    resolveInitialChatPageTab(location.state),
  );
  const [activityCache, setActivityCache] = useState<Record<string, ActivityRequest | null>>(() => {
    const seeded: Record<string, ActivityRequest | null> = {};
    chatPageActivityCache.forEach((entry, activityId) => {
      if (Date.now() - entry.fetchedAt <= CHAT_ACTIVITY_CACHE_TTL_MS) {
        seeded[activityId] = entry.value;
      }
    });
    return seeded;
  });
  const activityCacheRef = useRef(activityCache);
  const [chatDrafts, setChatDrafts] = useState<ChatDraftsByChatId>(() => readChatDrafts());
  const hasPrefetchedInitialActivities = useRef(false);
  const INITIAL_ACTIVITY_PREFETCH = 10;

  // Pagination state
  const [displayedGroupChatsCount, setDisplayedGroupChatsCount] = useState(20);
  const [displayedDirectChatsCount, setDisplayedDirectChatsCount] =
    useState(20);

  const CHATS_PER_PAGE = 20;

  const [isLoadingChats, setIsLoadingChats] = useState(true);

  useEffect(() => {
    const initialTab = (location.state as { initialTab?: "groups" | "chats" } | null)
      ?.initialTab;
    if (initialTab === "groups" || initialTab === "chats") {
      setActiveTab(initialTab);
    }
  }, [location.state]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(CHAT_PAGE_ACTIVE_TAB_STORAGE_KEY, activeTab);
    } catch {
      // Ignore storage failures (private mode / blocked storage).
    }
  }, [activeTab]);

  // Handle notification state passed from App.tsx
  useEffect(() => {
    if (location.state?.findDirectChatWithUser) {
      const userId = location.state.findDirectChatWithUser;
      const source = location.state.source || "unknown";

      // Trigger the direct chat finding with scroll hint
      setTimeout(async () => {
        try {
          const chatId = await createOrGetDirectChat(userId);
          navigate(`/chat/${chatId}?from=notification&scroll=latest`, {
            replace: true,
          });
        } catch (error) {
          crashlytics.logError(
            `Error creating or getting direct chat: ${JSON.stringify(getErrorMessage(error))}`,
            "ChatPage.tsx",
            error,
          );
        }
      }, 100);
    }
  }, [location.state, createOrGetDirectChat, navigate]);

  const { userChats, groupChats, directChats } = useMemo(() => {
    if (!currentUser) return { userChats: [], groupChats: [], directChats: [] };

    const allChatsForUser = getChatsForUser(currentUser.id);

    // Filter group chats - only check if user is a participant and has an activityId
    const validGroupChats = allChatsForUser.filter((chat) => {
      if (chat.activityId === null) return false; // Not a group chat
      if (chat.activityStatus === "inactive") return false; // Activity ended/deleted

      // Only show chat if user is still in the participants list
      if (!chat.participants.includes(currentUser.id)) return false;

      // Only show if there are messages OR more than 1 participant (someone has joined)
      const hasMessages = chat.messages && chat.messages.length > 0;
      const hasMultipleParticipants = chat.participants.length > 1;

      return hasMessages || hasMultipleParticipants;
    });

    const allDirectChats = allChatsForUser.filter(
      (chat) =>
        chat.activityId === null && chat.participants.includes(currentUser.id),
    );

    return {
      userChats: allChatsForUser,
      groupChats: validGroupChats,
      directChats: allDirectChats,
    };
  }, [currentUser, getChatsForUser]);

  // Mark chats as loaded once subscription data arrives
  useEffect(() => {
    setIsLoadingChats(false);
  }, [userChats.length]);

  useEffect(() => {
    activityCacheRef.current = activityCache;
  }, [activityCache]);

  useEffect(() => {
    const refreshDrafts = () => {
      setChatDrafts(readChatDrafts());
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshDrafts();
      }
    };

    window.addEventListener("storage", refreshDrafts);
    window.addEventListener("focus", refreshDrafts);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", refreshDrafts);
      window.removeEventListener("focus", refreshDrafts);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const ensureActivity = useCallback(
    async (activityId: string): Promise<ActivityRequest | null> => {
      if (!activityId) return null;

      const localCacheValue = activityCacheRef.current[activityId];
      if (localCacheValue !== undefined) {
        return localCacheValue;
      }

      const globallyCached = getCachedChatActivity(activityId);
      if (globallyCached !== undefined) {
        setActivityCache((prev) => {
          if (prev[activityId] !== undefined) return prev;
          return { ...prev, [activityId]: globallyCached };
        });
        return globallyCached;
      }

      const existingInFlight = chatPageActivityInFlight.get(activityId);
      if (existingInFlight) {
        return existingInFlight;
      }

      const fetchPromise = (async () => {
        try {
          const result = await refreshSingleActivity(activityId);
          const resolved = result ?? null;
          setCachedChatActivity(activityId, resolved);
          setActivityCache((prev) => {
            if (prev[activityId] !== undefined) return prev;
            return { ...prev, [activityId]: resolved };
          });
          return resolved;
        } catch (error) {
          crashlytics.logError(
            `Error prefetching activity ${activityId}: ${JSON.stringify(getErrorMessage(error))}`,
            "ChatPage.tsx",
            error,
          );
          setCachedChatActivity(activityId, null);
          setActivityCache((prev) => {
            if (prev[activityId] !== undefined) return prev;
            return { ...prev, [activityId]: null };
          });
          return null;
        } finally {
          chatPageActivityInFlight.delete(activityId);
        }
      })();

      chatPageActivityInFlight.set(activityId, fetchPromise);
      return fetchPromise;
    },
    [refreshSingleActivity],
  );

  const isActivityInactive = useCallback((activity?: ActivityRequest | null) => {
    if (!activity) return false;
    if (activity.isDeleted) return true;
    if (activity.isActive === false) return true;

    const startMs = new Date(activity.dateTime).getTime();
    const durationMs = Number(activity.duration || 0) * 60 * 1000;
    const endMs = startMs + durationMs;

    if (!Number.isFinite(endMs)) return false;
    return endMs < Date.now();
  }, []);

  const inactiveActivityIds = useMemo(() => {
    const inactiveIds = new Set<string>();
    Object.entries(activityCache).forEach(([id, activity]) => {
      if (isActivityInactive(activity)) inactiveIds.add(id);
    });
    return inactiveIds;
  }, [activityCache, isActivityInactive]);

  // Warm up the first screen of group chats to show organizer info immediately
  useEffect(() => {
    if (hasPrefetchedInitialActivities.current) return;
    const toPrefetch = groupChats
      .slice(0, INITIAL_ACTIVITY_PREFETCH)
      .map((chat) => chat.activityId)
      .filter((id): id is string => Boolean(id));

    if (toPrefetch.length === 0) return;
    hasPrefetchedInitialActivities.current = true;
    toPrefetch.forEach((id) => {
      ensureActivity(id);
    });
  }, [groupChats, ensureActivity]);

  const {
    onlineUsers,
    isLoadingOnlineUsers,
    participantUsersMap,
    resolveParticipants,
    refreshParticipants,
  } = useChatParticipants({ currentUser, userChats, getUsersByIds, getAllUsers });

  // With real-time subscriptions keeping chats current, no additional activity refresh is needed.

  // Refresh participant metadata when page regains focus or tab becomes visible
  useEffect(() => {
    if (!currentUser) return;
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshParticipants();
      }
    };
    const handleFocus = () => {
      refreshParticipants();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [currentUser, refreshParticipants]);

  const totalUnreadCount = currentUser
    ? getUnreadMessageCount(currentUser.id)
    : 0;

  const filteredGroupChats = useMemo(() => {
    const query = deferredSearchQuery.toLowerCase().trim();

    // Apply search filter
    const chatsToFilter = query
      ? groupChats.filter((chat) =>
        chat.activityTitle.toLowerCase().includes(query),
      )
      : groupChats;

    // Sort by most recent message (most active chats first)
    // Chats without messages will appear at the bottom
    return [...chatsToFilter]
      .sort((a, b) => {
        const lastMessageA =
          a.messages.length > 0
            ? new Date(a.messages[a.messages.length - 1].timestamp).getTime()
            : 0;
        const lastMessageB =
          b.messages.length > 0
            ? new Date(b.messages[b.messages.length - 1].timestamp).getTime()
            : 0;
        return lastMessageB - lastMessageA; // Most recent first
      });
  }, [deferredSearchQuery, groupChats]);

  const pendingVisibleGroupActivityIds = useMemo(() => {
    return filteredGroupChats
      .slice(0, displayedGroupChatsCount)
      .map((chat) => chat.activityId)
      .filter(
        (activityId): activityId is string =>
          Boolean(activityId) && activityCache[activityId] === undefined,
      );
  }, [filteredGroupChats, displayedGroupChatsCount, activityCache]);

  useEffect(() => {
    if (pendingVisibleGroupActivityIds.length === 0) return;
    pendingVisibleGroupActivityIds.forEach((activityId) => {
      ensureActivity(activityId);
    });
  }, [pendingVisibleGroupActivityIds, ensureActivity]);

  const isResolvingVisibleGroupActivities =
    pendingVisibleGroupActivityIds.length > 0;

  const stableFilteredGroupChats = useMemo(() => {
    return filteredGroupChats.filter((chat) => {
      if (!chat.activityId) return true;

      const activity = activityCache[chat.activityId];
      if (activity === undefined) return false;

      if (inactiveActivityIds.has(chat.activityId)) {
        return false;
      }

      return true;
    });
  }, [filteredGroupChats, activityCache, inactiveActivityIds]);

  const filteredDirectChats = useMemo(() => {
    if (!currentUser) return [];

    const query = deferredSearchQuery.toLowerCase().trim();

    // First filter: Only show chats with mutual followers
    const mutualFollowerChats = directChats.filter((chat) => {
      const otherParticipantId = chat.participants.find(
        (id) => id !== currentUser.id,
      );
      if (!otherParticipantId) return false;

      const otherParticipant = participantUsersMap.get(otherParticipantId);
      if (!otherParticipant) return false;

      // Check mutual following: current user follows them AND they follow current user
      const isMutualFollower =
        currentUser.following?.includes(otherParticipantId) &&
        otherParticipant.following?.includes(currentUser.id);

      return isMutualFollower;
    });

    // Second filter: Apply search query
    const chatsToFilter = query
      ? mutualFollowerChats.filter((chat) => {
        const otherParticipantId = chat.participants.find(
          (id) => id !== currentUser.id,
        );
        const otherParticipant = participantUsersMap.get(otherParticipantId!);
        return otherParticipant?.username.toLowerCase().includes(query);
      })
      : mutualFollowerChats;

    // Sort direct chats by last message time (most recent first)
    return [...chatsToFilter].sort((a, b) => {
      const lastMessageA =
        a.messages.length > 0
          ? new Date(a.messages[a.messages.length - 1].timestamp).getTime()
          : 0;
      const lastMessageB =
        b.messages.length > 0
          ? new Date(b.messages[b.messages.length - 1].timestamp).getTime()
          : 0;
      return lastMessageB - lastMessageA; // Most recent first
    });
  }, [deferredSearchQuery, directChats, currentUser, participantUsersMap]);

  // Paginated chats to display
  const displayedGroupChats = useMemo(() => {
    return stableFilteredGroupChats.slice(0, displayedGroupChatsCount);
  }, [stableFilteredGroupChats, displayedGroupChatsCount]);

  const displayedDirectChats = useMemo(() => {
    return filteredDirectChats.slice(0, displayedDirectChatsCount);
  }, [filteredDirectChats, displayedDirectChatsCount]);

  const hasMoreGroupChats =
    stableFilteredGroupChats.length > displayedGroupChatsCount;
  const hasMoreDirectChats =
    filteredDirectChats.length > displayedDirectChatsCount;

  const loadMoreGroupChats = useCallback(() => {
    setDisplayedGroupChatsCount((prev) => prev + CHATS_PER_PAGE);
  }, [CHATS_PER_PAGE]);

  const loadMoreDirectChats = useCallback(() => {
    setDisplayedDirectChatsCount((prev) => prev + CHATS_PER_PAGE);
  }, [CHATS_PER_PAGE]);

  // Reset pagination when search query or tab changes
  useEffect(() => {
    setDisplayedGroupChatsCount(CHATS_PER_PAGE);
    setDisplayedDirectChatsCount(CHATS_PER_PAGE);
  }, [searchQuery, activeTab]);

  // Get unread counts for each tab
  const groupUnreadCount = currentUser
    ? getUnreadGroupChatCount(currentUser.id)
    : 0;
  const directUnreadCount = currentUser
    ? getUnreadDirectChatCount(currentUser.id)
    : 0;

  const tabs = useMemo(
    () => [
      {
        id: "groups",
        name: "Activities",
        icon: UsersIcon,
        count: groupChats.length,
        unreadCount: groupUnreadCount,
      },
      {
        id: "chats",
        name: "Friends",
        icon: MessageCircleIcon,
        count: directChats.length,
        unreadCount: directUnreadCount,
      },
    ] as const,
    [groupChats.length, directChats.length, groupUnreadCount, directUnreadCount],
  );

  const groupChatRows = useMemo(
    () =>
      displayedGroupChats.map((chat) => ({
        chat,
        participants: resolveParticipants(chat, currentUser?.id),
        activity: chat.activityId ? activityCache[chat.activityId] : undefined,
        draft: chatDrafts[chat.id],
        unreadCount: getUnreadMessageCountForChat(chat.id),
      })),
    [
      displayedGroupChats,
      resolveParticipants,
      currentUser?.id,
      activityCache,
      chatDrafts,
      getUnreadMessageCountForChat,
    ],
  );

  const directChatRows = useMemo(
    () =>
      displayedDirectChats.map((chat) => ({
        chat,
        participants: resolveParticipants(chat, currentUser?.id),
        activity: chat.activityId ? activityCache[chat.activityId] : undefined,
        draft: chatDrafts[chat.id],
        unreadCount: getUnreadMessageCountForChat(chat.id),
      })),
    [
      displayedDirectChats,
      resolveParticipants,
      currentUser?.id,
      activityCache,
      chatDrafts,
      getUnreadMessageCountForChat,
    ],
  );

  const renderChatList = useCallback((
    chatRows: Array<{
      chat: Chat;
      participants: User[];
      activity?: ActivityRequest | null;
      draft?: ChatDraftRecord;
      unreadCount: number;
    }>,
    hasMore: boolean,
    onLoadMore: () => void,
    emptyTitle: string,
    emptyMessage: string,
    remaining: number,
  ) => {
    if (chatRows.length === 0) {
      return <EmptyState title={emptyTitle} message={emptyMessage} />;
    }

    return (
      <>
        {chatRows.map(({ chat, participants, activity, draft, unreadCount }) => (
          <ChatListItem
            key={chat.id}
            chat={chat}
            participants={participants}
            activity={activity}
            onNeedActivity={ensureActivity}
            draft={draft}
            unreadCount={unreadCount}
            currentUserId={currentUser?.id}
            returnToTab={chat.activityId === null ? "chats" : "groups"}
          />
        ))}
        {hasMore && (
          <button
            onClick={onLoadMore}
            className="w-full py-3 text-sm font-medium text-primary hover:bg-gray-50 rounded-lg transition-colors"
          >
            Load More ({remaining} more)
          </button>
        )}
      </>
    );
  }, [ensureActivity, currentUser?.id]);

  const groupListContent = useMemo(
    () =>
      renderChatList(
        groupChatRows,
        hasMoreGroupChats,
        loadMoreGroupChats,
        "No Activity Chats Yet",
        "When you join an activity, your group chats will appear here.",
        stableFilteredGroupChats.length - displayedGroupChatsCount,
      ),
    [
      renderChatList,
      groupChatRows,
      hasMoreGroupChats,
      loadMoreGroupChats,
      stableFilteredGroupChats.length,
      displayedGroupChatsCount,
    ],
  );

  const directListContent = useMemo(
    () =>
      renderChatList(
        directChatRows,
        hasMoreDirectChats,
        loadMoreDirectChats,
        "No Friend Chats",
        "When you and another user follow each other, you can chat here.",
        filteredDirectChats.length - displayedDirectChatsCount,
      ),
    [
      renderChatList,
      directChatRows,
      hasMoreDirectChats,
      loadMoreDirectChats,
      filteredDirectChats.length,
      displayedDirectChatsCount,
    ],
  );

  if (!currentUser) return null;

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Header */}
      <header
        className="flex-shrink-0 bg-white/80 backdrop-blur-lg shadow-sm border-b border-gray-200/80"
        style={{ position: "sticky", top: "0", zIndex: 20 }}
      >
        {/* Title */}
        <div className="p-3 flex items-center justify-center">
          <div className="flex items-center">
            <h1 className="text-xl font-bold text-gray-900">Conversations</h1>
            {totalUnreadCount > 0 && (
              <span className="ml-2 bg-primary/10 text-primary text-xs font-bold px-2 py-0.5 rounded-full">
                {totalUnreadCount} new
              </span>
            )}
          </div>
        </div>
        {/* Search Bar */}
        <div className="p-3 border-t border-gray-200/80">
          <div className="relative">
            <input
              type="text"
              placeholder="Search conversations..."
              className="w-full bg-gray-100 rounded-full pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 placeholder-gray-500 border border-gray-200"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              name="chat-search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              enterKeyHint="search"
            />
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
              <SearchIcon className="w-5 h-5" />
            </div>
          </div>
        </div>
      </header>

      {/* Active Now Bar */}
      <section className="py-2 border-b border-gray-200 bg-white min-h-[76px]">
        <h2 className="px-4 pb-1 text-xs font-semibold text-gray-900">
          Active Now
        </h2>
        <div className="px-4">
          {isLoadingOnlineUsers ? (
            <div className="flex space-x-2 overflow-x-auto pb-1 hide-scrollbar">
              {[...Array(5)].map((_, idx) => (
                <div key={idx} className="w-12 flex-shrink-0">
                  <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse" />
                  <div className="h-2 mt-1 rounded bg-gray-200 animate-pulse" />
                </div>
              ))}
            </div>
          ) : onlineUsers.length > 0 ? (
            <div className="flex space-x-2 overflow-x-auto pb-1 hide-scrollbar">
              {onlineUsers.map((user) => (
                <ActiveNowCircle key={user.id} user={user} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 pb-1">No friends active right now.</p>
          )}
        </div>
      </section>

      {/* Tabs */}
      <div className="p-2 bg-white sticky top-[109px] z-10 border-b border-gray-200">
        <LayoutGroup>
          <div className="p-1 bg-gray-100 rounded-full flex items-center relative shadow-inner">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full relative z-10 py-1.5 text-sm font-bold transition-colors rounded-full flex items-center justify-center gap-2 ${activeTab === tab.id ? "text-white" : "text-gray-500 hover:text-gray-900"}`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="pill-chat"
                    className="absolute inset-0 bg-primary rounded-full shadow-md"
                    style={{ borderRadius: 9999 }}
                    transition={{
                      type: "spring",
                      stiffness: 350,
                      damping: 30,
                    }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-1.5">
                  <tab.icon className="w-4 h-4" />
                  {tab.name}
                  {tab.unreadCount > 0 && (
                    <span
                      className={`ml-1 text-xs font-bold px-1.5 py-0.5 rounded-full ${activeTab === tab.id
                        ? "bg-white/20 text-white"
                        : "bg-primary text-white"
                        }`}
                    >
                      {tab.unreadCount}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </LayoutGroup>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto hide-scrollbar pb-20">
        <div className="max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: activeTab === "groups" ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: activeTab === "groups" ? 20 : -20 }}
              transition={{ duration: 0.2 }}
              className="p-2"
            >
              {isLoadingChats || (activeTab === "groups" && isResolvingVisibleGroupActivities) ? (
                <div className="flex flex-col gap-2">
                  {[...Array(6)].map((_, idx) => (
                    <div key={idx} className="animate-pulse h-16 bg-gray-100 rounded-lg" />
                  ))}
                </div>
              ) : activeTab === "groups" ? (
                groupListContent
              ) : (
                directListContent
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default ChatPage;
